import { useEffect, useState } from "react";
import { apiUrl } from "../../utils/jwt-storage";

type Way = { key: string; label: string; url: string; icon: string };

// The desktop shell and the phone app run on an address of their own, where
// a sign-in that leaves for another site could not come back.
const onOwnServer = () => {
  const w = window as unknown as { Capacitor?: unknown; __TAURI__?: unknown };
  const shell =
    (window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost") &&
    window.location.port === "17080";
  return !w.Capacitor && !w.__TAURI__ && !shell;
};

/** Other ways to sign in (offered by an installed extension), as buttons
 * under the card's form. ``query`` is added to each way's address;
 * ``refuse`` may return true to keep the person on the card (it says why
 * itself). ``also`` is one more way the card found on its own. */
export const OtherWays = ({
  query,
  refuse,
  also,
}: {
  query?: Record<string, string>;
  refuse?: () => boolean;
  also?: { url: string; label: string } | null;
}) => {
  const [ways, setWays] = useState<Way[]>([]);

  useEffect(() => {
    if (!onOwnServer()) return;
    let alive = true;
    fetch(apiUrl("/api/auth/sign-in-ways/"), { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : { ways: [] }))
      .then((body) => {
        if (alive && Array.isArray(body?.ways)) setWays(body.ways);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (!ways.length && !also) return null;

  const address = (url: string) => {
    const extra = new URLSearchParams(query ?? {}).toString();
    return extra ? `${url}${url.includes("?") ? "&" : "?"}${extra}` : url;
  };

  return (
    <div className="auth-ways" data-testid="sign-in-ways">
      <div className="auth-or" aria-hidden="true">
        <span>or</span>
      </div>
      {also && (
        <a className="auth-way" href={also.url} data-external data-testid="sign-in-elsewhere">
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
          >
            <path d="M4 20V6l8-3 8 3v14" strokeLinejoin="round" />
            <path d="M9 20v-5h6v5M9 9h.01M15 9h.01M9 12h.01M15 12h.01" strokeLinecap="round" />
          </svg>
          <span>Continue with {also.label}</span>
        </a>
      )}
      {ways.map((way) => (
        <a
          key={way.key}
          className="auth-way"
          href={address(way.url)}
          data-external
          data-testid={`sign-in-way-${way.key}`}
          onClick={(e) => {
            if (refuse?.()) e.preventDefault();
          }}
        >
          {way.icon ? <img src={way.icon} alt="" width="18" height="18" /> : null}
          <span>Continue with {way.label}</span>
        </a>
      ))}
    </div>
  );
};
